//@flow

import {actions} from './duck';
import {
  getMultichatFunnelService,
  createMultichatFunnelService,
  getMultichatFunnelStagesService,
  getMultichatFunnelsService,
  setMultichatFunnelMainLaneService,
  createMultichatFunnelStageService,
  editMultichatFunnelStageService,
  editMultichatFunnelService,
  getLaneConversationsService,
  sortFunnelConversationsService,
  deleteFunnelLaneService,
} from './promises';
import {useActions} from '../../../utils/globalStore';
import {useEffect, useState, useCallback} from 'react';

import {useSelector} from 'react-redux';
import {DEFAULT_MULTICHAT_FUNNELS_STAGES} from './constants';
import {useAlert} from '../../../elements/Alert/AlertContext';

import type {
  CreateMultichatFunnelStage,
  MultichatFunnelLane,
  SortFunnelConversationsRequest,
  Conversation,
} from '../../../entities';
import type {RootState} from '../../';
import type {DragAndDropConversationsResult} from './duck';

export function useInitializeMultichatFunnels() {
  const initMultichatFunnels = useActions(actions.initMultichatFunnels);

  useEffect(() => {
    const getMultichatFunnelData = async () => {
      const multichatFunnels = await getMultichatFunnelsService();
      const funnelStages = await getMultichatFunnelStagesService();
      const firstStages = funnelStages.slice(0, 5);

      initMultichatFunnels(multichatFunnels, firstStages);

      return multichatFunnels;
    };

    getMultichatFunnelData().catch(() => {
      initMultichatFunnels([]);
    });
  }, [initMultichatFunnels]);
}

const multichatFunnelSelector = (state: RootState) =>
  state.multichatFunnel.funnels;

export function useMultichatFunnels() {
  const funnels = useSelector(multichatFunnelSelector);

  return {funnels};
}

const currentMultichatFunnelSelector = (state: RootState) =>
  state.multichatFunnel.currentFunnel;

export function useCurrentMultichatFunnel() {
  return useSelector(currentMultichatFunnelSelector);
}

export function useCurrentMultichatFunnelActions(search?: string) {
  const currentFunnel = useCurrentMultichatFunnel();

  const {showAlert} = useAlert();

  const boundActions = useActions(actions);
  const addLaneToCurrentMultichatFunnelAction =
    boundActions.addLaneToCurrentMultichatFunnel;
  const dragAndDropConversationsAction = boundActions.dragAndDropConversations;
  const addConversationsToLane = boundActions.addConversationsToLane;
  const setFunnelMainLane = boundActions.setFunnelMainLane;
  const deleteFunnelLaneAction =
    boundActions.deleteLaneFromCurrentMultichatFunnel;
  const reorderFunnelLanesAction = boundActions.reorderFunnelLanes;
  const removeConversationFromLaneAction =
    boundActions.removeConversationFromLane;

  const [loading, setLoading] = useState(false);

  const changeMainFunnelLane = (stageId: string) => {
    if (!currentFunnel) return;
    setMultichatFunnelMainLaneService(currentFunnel.id, stageId).then(() => {
      setFunnelMainLane(stageId);
    });
  };

  const addLane = async (newStage: CreateMultichatFunnelStage) => {
    if (!currentFunnel) return;
    const prevLanesIds = currentFunnel.lanes.map(lane => ({
      funnelLaneId: lane.id,
    }));

    const funnel = await editMultichatFunnelService(currentFunnel.id, {
      funnelLanes: prevLanesIds.concat({
        funnelStage: newStage,
      }),
    });

    const createdLane = funnel.lanes[funnel.lanes.length - 1];

    const newLane: MultichatFunnelLane = {
      conversations: [],
      id: createdLane.id,
      number: createdLane.number,
      stage: createdLane.stage,
      conversationPagination: {
        page: 0,
        size: 20,
        total: 0,
      },
    };

    addLaneToCurrentMultichatFunnelAction(newLane);
  };

  const addLaneConversationsPage = useCallback(
    async (laneId: string) => {
      setLoading(true);
      try {
        const funnelLanePagination = currentFunnel?.lanes.find(
          lane => lane.id === laneId
        )?.conversationPagination;

        if (!funnelLanePagination) return;

        const hasMoreItems =
          funnelLanePagination.total >
          funnelLanePagination.size * (funnelLanePagination.page + 1);

        if (!hasMoreItems) return;

        const conversations = await getLaneConversationsService(
          currentFunnel.id,
          laneId,
          {
            page: funnelLanePagination.page + 1,
            size: funnelLanePagination.size,
            identifier: search || undefined,
          }
        );

        addConversationsToLane(laneId, conversations.data);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    },
    [addConversationsToLane, currentFunnel, search]
  );

  const dragAndDropConversations = (result: DragAndDropConversationsResult) => {
    if (!currentFunnel) return;
    if (!result.destination) return;

    const sortResult: SortFunnelConversationsRequest = {
      laneConversationOrigin: {
        crmConversationId: result.source.conversationId,
        funnelLaneId: result.source.laneId,
      },
      laneConversationTarget: {
        crmConversationId: result.destination.conversationId,
        funnelLaneId: result.destination.laneId,
      },
    };

    sortFunnelConversationsService(currentFunnel.id, sortResult).then(() => {
      dragAndDropConversationsAction(result);
    });
  };

  const deleteFunnelLane = async (laneId: string) => {
    setLoading(true);
    try {
      await deleteFunnelLaneService(laneId);
      deleteFunnelLaneAction(laneId);
    } catch (error) {
      setLoading(false);
      showAlert('Error al eliminar la lista', {
        type: 'error',
      });
    }
  };

  const reorderFunnelLanes = async (newFunnelLanesIds: string[]) => {
    if (!currentFunnel) return;

    // Create new array to force re-render, though ideally this should be handled in duck
    const newLanes = [...currentFunnel.lanes].sort(
      (a, b) =>
        newFunnelLanesIds.indexOf(a.id) - newFunnelLanesIds.indexOf(b.id)
    );

    // Assuming there's a setLanes action or we can just call edit service
    try {
      reorderFunnelLanesAction(newLanes);

      const payload = {
        funnelLanes: newLanes.map(lane => ({
          funnelLaneId: lane.id,
          funnelStageId: lane.stage.id,
          funnelStage: {
            label: lane.stage.label,
            color: lane.stage.color,
          },
        })),
      };

      await editMultichatFunnelService(currentFunnel.id, payload);
    } catch (error) {
      console.error('Error in reorderFunnelLanes:', error);
      showAlert('Error al reordenar las listas', {
        type: 'error',
      });
    }
  };

  const removeConversationFromLane = (
    laneId: string,
    conversationId: string
  ) => {
    removeConversationFromLaneAction(laneId, conversationId);
  };

  return {
    changeMainFunnelLane,
    dragAndDropConversations,
    addLaneConversationsPage,
    deleteFunnelLane,
    reorderFunnelLanes,
    addLane,
    loading,
    removeConversationFromLane,
  };
}

export function useInitializeCurrentFunnelDetails(
  funnelId?: string,
  search?: string
) {
  const {initCurrentMultichatFunnelConversations} = useActions(actions);

  const setCurrentMultichatFunnel = useActions(
    actions.setCurrentMultichatFunnel
  );

  useEffect(() => {
    if (!funnelId) return;

    getMultichatFunnelService(funnelId).then(funnelData => {
      setCurrentMultichatFunnel(funnelData);
      const laneConversationsPromises = funnelData.lanes.map(lane => {
        return getLaneConversationsService(funnelId, lane.id, {
          size: 20,
          page: 0,
          identifier: search || undefined,
        }).then(conversations => ({
          ...conversations,
          laneId: lane.id,
        }));
      });

      Promise.all(laneConversationsPromises).then(conversations => {
        const newLanes = funnelData.lanes.map(lane => {
          const laneConversations = conversations.find(
            item => item.laneId === lane.id
          );

          return {
            ...lane,
            conversations: laneConversations?.data || [],
            conversationPagination: laneConversations?.pagination,
          };
        });
        initCurrentMultichatFunnelConversations(newLanes);
      });
    });
  }, [
    funnelId,
    search,
    initCurrentMultichatFunnelConversations,
    setCurrentMultichatFunnel,
  ]);
}

const multichatFunnelStagesSelector = (state: RootState) =>
  state.multichatFunnel.funnelStages;

export function useMultichatFunnelStages() {
  const funnelStages = useSelector(multichatFunnelStagesSelector);

  return {funnelStages};
}

const funnelStageActions = {
  createFunnelStageAction: actions.createMultichatFunnelStage,
  editFunnelStageAction: actions.editMultichatFunnelStage,
};

export function useMultichatFunnelsStageActions() {
  const {createFunnelStageAction, editFunnelStageAction} = useActions(
    funnelStageActions
  );

  const {showAlert} = useAlert();

  const [loading, setLoading] = useState(false);

  const createFunnelStage = async (stage: CreateMultichatFunnelStage) => {
    setLoading(true);
    try {
      const stageResult = await createMultichatFunnelStageService(stage);
      // const stages = await getMultichatFunnelStagesService();
      createFunnelStageAction(stageResult);
      return stageResult;
    } catch (error) {
      setLoading(false);
      showAlert('Error al crear la lista', {
        type: 'error',
      });
    }
  };

  const editFunnelStage = async (
    stageId: string,
    stage: CreateMultichatFunnelStage
  ) => {
    setLoading(true);
    try {
      const stageResult = await editMultichatFunnelStageService(stageId, stage);
      // const stages = await getMultichatFunnelStagesService();
      editFunnelStageAction(stageResult);
    } catch (error) {
      setLoading(false);

      showAlert('Error al editar la lista', {
        type: 'error',
      });
    }
  };

  return {
    createFunnelStage,
    editFunnelStage,
    loading,
  };
}

export function useMultichatFunnelActions() {
  const {funnelStages} = useMultichatFunnelStages();
  const createMultichatFunnelAction = useActions(actions.createMultichatFunnel);

  const {showAlert} = useAlert();

  const createFunnel = async (funnelName: string) => {
    try {
      console.log({funnelStages});
      const stages =
        funnelStages?.length > 0
          ? funnelStages.map(stage => ({
              funnelStageId: stage.id,
            }))
          : DEFAULT_MULTICHAT_FUNNELS_STAGES.map(stage => ({
              funnelStage: stage,
            }));

      const funnel = await createMultichatFunnelService({
        // $FlowFixMe
        funnelLanes: stages,
        name: funnelName,
      });

      const laneId = funnel.lanes[0].id;

      await setMultichatFunnelMainLaneService(funnel.id, laneId);

      await createMultichatFunnelAction(funnel);
    } catch (error) {
      showAlert('Error al crear el funnel', {
        type: 'error',
      });
    }
  };

  return {
    createFunnel,
  };
}

const funnelConversationActins = {
  updateFunnelConversationAction: actions.updateFunnelConversation,
  closeFunnelConversationAction: actions.closeFunnelConversation,
};

export function useFunnelConversationAction() {
  const {
    updateFunnelConversationAction,
    closeFunnelConversationAction,
  } = useActions(funnelConversationActins);

  const updateFunnelConversation = (conversation: Conversation) => {
    updateFunnelConversationAction(conversation);
  };

  const closeFunnelConversation = (conversationId: string) => {
    closeFunnelConversationAction(conversationId);
  };

  return {
    updateFunnelConversation,
    closeFunnelConversation,
  };
}
